import * as z from 'zod/mini';
import {
  CUSTOM_FIELD_KEY_MAX_LENGTH,
  CUSTOM_FIELD_LABEL_MAX_LENGTH,
  CUSTOM_FIELD_KINDS,
  CUSTOM_FIELD_MAX_OPTIONS,
  CUSTOM_FIELD_OPTION_MAX_LENGTH,
  CUSTOM_TYPE_MAX_FIELDS,
  CUSTOM_TYPE_NAME_MAX_LENGTH,
  CUSTOM_VALUE_MAX_LENGTH,
} from '../constants.ts';
import { dateSchema, idSchema, timestampSchema } from './common.ts';

const label = z
  .string()
  .check(z.trim(), z.minLength(1), z.maxLength(CUSTOM_FIELD_LABEL_MAX_LENGTH));
const option = z
  .string()
  .check(z.trim(), z.minLength(1), z.maxLength(CUSTOM_FIELD_OPTION_MAX_LENGTH));
export const customTypeNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1), z.maxLength(CUSTOM_TYPE_NAME_MAX_LENGTH));
const customFieldKeySchema = z.string().check(
  z.regex(/^[A-Za-z][A-Za-z0-9_]*$/u),
  z.maxLength(CUSTOM_FIELD_KEY_MAX_LENGTH),
  z.refine(
    (key) => key !== 'prototype' && !Object.hasOwn(Object.prototype, key),
    'Reserved field key',
  ),
);
export const customFieldSchema = z
  .strictObject({
    key: customFieldKeySchema,
    label,
    kind: z.enum(CUSTOM_FIELD_KINDS),
    required: z.boolean(),
    options: z.array(option).check(z.maxLength(CUSTOM_FIELD_MAX_OPTIONS)),
  })
  .check(
    z.refine(
      (field) =>
        field.kind === 'choice'
          ? field.options.length > 0 && new Set(field.options).size === field.options.length
          : field.options.length === 0,
      'Only choices have options; choices need distinct options',
    ),
  );
export type CustomField = z.infer<typeof customFieldSchema>;
export const customFieldsSchema = z.array(customFieldSchema).check(
  z.minLength(1),
  z.maxLength(CUSTOM_TYPE_MAX_FIELDS),
  z.refine(
    (fields) => new Set(fields.map((field) => field.key)).size === fields.length,
    'Field keys must be unique',
  ),
);
// Inspect raw keys before Zod's record parser discards __proto__ for safety.
// Invalid fields must be refused, never silently omitted from a successful save.
export const customValuesSchema = z.pipe(
  z
    .unknown()
    .check(
      z.refine(
        (value) =>
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value) &&
          Object.keys(value).every((key) => customFieldKeySchema.safeParse(key).success),
        'Custom values require valid field keys',
      ),
    ),
  z
    .record(
      customFieldKeySchema,
      z.union([z.string().check(z.maxLength(CUSTOM_VALUE_MAX_LENGTH)), z.number(), z.boolean()]),
    )
    .check(
      z.refine(
        (values) => Object.keys(values).length <= CUSTOM_TYPE_MAX_FIELDS,
        'Too many custom values',
      ),
    ),
);
export type CustomValues = z.infer<typeof customValuesSchema>;
export const customTypeInputSchema = z.strictObject({
  name: customTypeNameSchema,
  target: z.enum(['item', 'record']),
  fields: customFieldsSchema,
});
export type CustomTypeInput = z.infer<typeof customTypeInputSchema>;
export const customTypeSchema = z.object({
  ...customTypeInputSchema.shape,
  id: idSchema,
  archived: z.boolean(),
  used: z.optional(z.boolean()),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type CustomType = z.infer<typeof customTypeSchema>;
export const customTypePatchSchema = z.strictObject({
  name: z.optional(customTypeNameSchema),
  archived: z.optional(z.boolean()),
  fields: z.optional(customFieldsSchema),
});
export type CustomTypePatch = z.infer<typeof customTypePatchSchema>;
export const customTypeListSchema = z.object({ types: z.array(customTypeSchema) });
export const customTypeEnvelopeSchema = z.object({ customType: customTypeSchema });

/** Edits to an in-use layout must accept every value its previous contract accepted. */
export function customFieldsCompatible(
  before: readonly CustomField[],
  after: readonly CustomField[],
): boolean {
  const previous = new Map(before.map((field) => [field.key, field]));
  return (
    before.every((field) => {
      const next = after.find((entry) => entry.key === field.key);
      return (
        next?.kind === field.kind &&
        (!next.required || field.required) &&
        field.options.every((option) => next.options.includes(option))
      );
    }) && after.every((field) => previous.has(field.key) || !field.required)
  );
}

/** The immutable definition drives both the form and the database write boundary. */
export function customValueIssues(
  fields: readonly CustomField[],
  values: Readonly<CustomValues>,
): string[] {
  const issues: string[] = [];
  const keys = new Set(fields.map((field) => field.key));
  for (const key of Object.keys(values)) {
    if (!keys.has(key)) issues.push(`Unknown custom field: ${key}`);
  }
  for (const field of fields) {
    const value = values[field.key];
    if (value === undefined || (typeof value === 'string' && value.trim() === '')) {
      if (field.required) issues.push(`${field.label}: required`);
      continue;
    }
    let valid: boolean;
    switch (field.kind) {
      case 'number': {
        valid = typeof value === 'number' && Number.isFinite(value);
        break;
      }
      case 'boolean': {
        valid = typeof value === 'boolean';
        break;
      }
      case 'date': {
        valid = z.safeParse(dateSchema, value).success;
        break;
      }
      case 'choice': {
        valid = typeof value === 'string' && field.options.includes(value);
        break;
      }
      case 'url': {
        valid =
          typeof value === 'string' && z.safeParse(z.url({ protocol: /^https?$/u }), value).success;
        break;
      }
      case 'text': {
        valid = typeof value === 'string' && value.length <= CUSTOM_VALUE_MAX_LENGTH;
        break;
      }
    }
    if (!valid) issues.push(`${field.label}: expected ${field.kind}`);
  }
  return issues;
}

/** Stable display values, including an explicit false rather than an empty cell. */
export function customValueText(value: string | number | boolean): string {
  return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
}

/** Resolve and validate the same assignment on the Worker and in an offline form. */
export function customAssignmentIssues(
  definition: CustomType | null,
  target: 'item' | 'record',
  id: string | null | undefined,
  values: CustomValues,
  previousId?: string | null,
): string[] {
  if (id === null || id === undefined)
    return Object.keys(values).length === 0
      ? []
      : ['Choose a custom asset type before setting its fields'];
  if (definition?.id !== id || definition.target !== target)
    return ['Custom asset type does not exist for this record'];
  if (definition.archived && previousId !== id) return ['This custom asset type is archived'];
  return customValueIssues(definition.fields, values);
}
